import React, { useState, useMemo } from 'react';
import { Task } from '../../types/task';
import { Connection } from '../../types/connection';
import { SnsTopicArn } from './SnsTopicArn';
import { appConfig } from '../../config';

export interface IntegrationGuideProps {
  task: Task;
  snsTopicArn?: string;
  isCreatingSns?: boolean;
  connection?: Connection;
}

// ---------------------------------------------------------------------------
// Clipboard helper
// ---------------------------------------------------------------------------
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  }
}

// ---------------------------------------------------------------------------
// Icons (same SVGs used by SnsTopicArn for consistency)
// ---------------------------------------------------------------------------
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="currentColor"
    className={`text-neutral-400 dark:text-slate-500 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
  >
    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
  </svg>
);

// ---------------------------------------------------------------------------
// CopyableField — label + mono value + copy button
// ---------------------------------------------------------------------------
function CopyableField({ label, value, placeholder }: {
  label: string;
  value?: string;
  placeholder?: string;
}) {
  const [copied, setCopied] = useState(false);
  const displayValue = value || placeholder;
  const isMissing = !value;

  const handleCopy = async () => {
    if (!value) return;
    if (await copyToClipboard(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
        {label}
      </label>
      <div className="
        flex items-center gap-2
        bg-white dark:bg-slate-900
        border border-neutral-200 dark:border-slate-700
        rounded px-3 py-2
      ">
        <code className={`
          flex-1 text-[13px] font-mono break-all
          ${isMissing
            ? 'text-neutral-400 dark:text-slate-500 italic'
            : 'text-neutral-800 dark:text-slate-200'
          }
        `}>
          {displayValue}
        </code>
        {value && (
          <button
            type="button"
            onClick={handleCopy}
            className={`
              flex-shrink-0 w-7 h-7 flex items-center justify-center rounded
              transition-all duration-150
              ${copied
                ? 'text-success-600 dark:text-success-400'
                : 'text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-slate-800 hover:text-neutral-700 dark:hover:text-neutral-200'
              }
            `}
            title={copied ? 'Copied!' : 'Copy'}
            aria-label={copied ? 'Copied to clipboard' : `Copy ${label}`}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CodeBlock — titled code block with header bar + copy button
// ---------------------------------------------------------------------------
function CodeBlock({ code, title }: { code: string; title?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (await copyToClipboard(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="rounded border border-neutral-200 dark:border-slate-700 overflow-hidden">
      {title && (
        <div className="
          flex items-center justify-between px-3 py-2
          bg-neutral-100 dark:bg-slate-800
          border-b border-neutral-200 dark:border-slate-700
        ">
          <span className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide">
            {title}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className={`
              flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
              transition-all duration-150
              ${copied
                ? 'text-success-600 dark:text-success-400'
                : 'text-neutral-500 dark:text-slate-400 hover:bg-neutral-200 dark:hover:bg-slate-700 hover:text-neutral-700 dark:hover:text-slate-200'
              }
            `}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy</>}
          </button>
        </div>
      )}
      <pre className="
        p-3 bg-white dark:bg-slate-900
        text-[13px] font-mono text-neutral-800 dark:text-slate-200
        overflow-x-auto whitespace-pre
      ">
        {code}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IAM policy generator (fallback when backend hasn't provided one)
// ---------------------------------------------------------------------------
function generateIamPolicy(
  direction: 'inbound' | 'outbound',
  bucketArn?: string,
  bucketName?: string,
  kmsKeyArn?: string,
  snsTopicArn?: string,
  s3Path?: string
): Record<string, any> {
  const bucket = bucketArn
    || (bucketName ? `arn:aws:s3:::${bucketName}` : 'arn:aws:s3:::<BUCKET_NAME>');
  const kms = kmsKeyArn || '<KMS_KEY_ARN>';
  const sns = snsTopicArn || '<SNS_TOPIC_ARN>';
  const path = s3Path || '<PATH>/';

  if (direction === 'outbound') {
    return {
      Version: '2012-10-17',
      Statement: [
        {
          Sid: 'AllowS3PutObject',
          Effect: 'Allow',
          Action: ['s3:PutObject'],
          Resource: `${bucket}/${path}*`,
        },
        {
          Sid: 'AllowKmsEncrypt',
          Effect: 'Allow',
          Action: ['kms:GenerateDataKey', 'kms:Encrypt'],
          Resource: kms,
        },
        {
          Sid: 'AllowSnsSubscribe',
          Effect: 'Allow',
          Action: ['sns:Subscribe'],
          Resource: sns,
        },
      ],
    };
  }

  return {
    Version: '2012-10-17',
    Statement: [
      {
        Sid: 'AllowS3GetObject',
        Effect: 'Allow',
        Action: ['s3:GetObject'],
        Resource: `${bucket}/${path}*`,
      },
      {
        Sid: 'AllowKmsDecrypt',
        Effect: 'Allow',
        Action: ['kms:Decrypt'],
        Resource: kms,
      },
      {
        Sid: 'AllowSnsSubscribe',
        Effect: 'Allow',
        Action: ['sns:Subscribe'],
        Resource: sns,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// IntegrationGuide — main component
// ---------------------------------------------------------------------------
export function IntegrationGuide({ task, snsTopicArn, isCreatingSns, connection }: IntegrationGuideProps) {
  const [showPolicy, setShowPolicy] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showPartnerGuide, setShowPartnerGuide] = useState(false);
  const [showDeveloperGuide, setShowDeveloperGuide] = useState(false);
  const [partnerBlockCopied, setPartnerBlockCopied] = useState(false);

  const integration = task.integration;
  const isOutbound = task.transferDirection === 'outbound';
  const isHostedInbound = connection?.hostingType === 'hosted' && task.transferDirection === 'inbound';

  // Resolve resource values from integration object or fallback from task data
  const resources = useMemo(() => {
    const effectiveSns = snsTopicArn || integration?.snsTopicArn || task.snsTopicArn;
    const prefix = task.prefix;

    return {
      bucketName: integration?.bucketName,
      bucketArn: integration?.bucketArn,
      kmsKeyArn: integration?.kmsKeyArn,
      snsTopicArn: effectiveSns,
      region: integration?.region,
      accountId: integration?.accountId,
      dropPath: integration?.dropPath || (prefix ? `staging/outbound/${prefix}/` : undefined),
      archivePath: integration?.archivePath || (prefix ? `archive/inbound/${prefix}/` : undefined),
    };
  }, [integration, snsTopicArn, task.snsTopicArn, task.prefix]);

  // Extract SFTP connection details for hosted inbound
  const sftpDetails = useMemo(() => {
    if (!isHostedInbound || !connection) return null;

    // Extract username from Transfer Family user ARN: .../<serverId>/<username>
    const userArn = connection.transferFamilyUserArn;
    let username: string | undefined;
    let serverId: string | undefined;
    if (userArn) {
      const parts = userArn.split('/');
      username = parts[parts.length - 1];
      serverId = parts.length >= 2 ? parts[parts.length - 2] : undefined;
    }

    // Build endpoint: prefer public SFTP domain from config, fallback to server ID
    const endpoint = appConfig?.sftpEndpoint
      || (serverId ? `${serverId}.server.transfer.us-east-1.amazonaws.com` : undefined);

    return {
      endpoint,
      username,
      port: '22',
      hasPublicKey: !!connection.config.publicKeyContent,
      uploadPath: task.prefix ? `inbound/${task.prefix}/` : 'inbound/',
      hasIpRestrictions: !!(connection.config.allowedIpRanges?.length &&
        !connection.config.allowedIpRanges.every((ip: string) => ip === '0.0.0.0/0')),
      ipRanges: connection.config.allowedIpRanges || [],
    };
  }, [isHostedInbound, connection, task.prefix]);

  // Direction-specific quick-start steps (moved before early return for hook ordering)
  const steps = isOutbound
    ? [
        'Attach the IAM policy below to your Lambda execution role',
        'Drop files to the S3 path shown below (the file name is up to you)',
        'TransferHub will automatically process and deliver the file to the partner',
        'Subscribe to the SNS topic to receive delivery confirmations',
      ]
    : [
        'Attach the IAM policy below to your Lambda execution role',
        'Subscribe an SQS queue to the SNS topic to receive FILE_READY notifications',
        'When notified, read the file from the archive path in the S3 bucket',
        'The SNS message includes the exact S3 key, bucket, and file metadata',
      ];

  const s3Path = isOutbound ? resources.dropPath : resources.archivePath;
  const s3PathLabel = isOutbound ? 'Drop Path' : 'Archive Path';

  // IAM policy — prefer backend-provided, generate fallback
  const iamPolicy = useMemo(() => {
    if (integration?.iamPolicy) return integration.iamPolicy;
    return generateIamPolicy(
      task.transferDirection,
      resources.bucketArn,
      resources.bucketName,
      resources.kmsKeyArn,
      resources.snsTopicArn,
      s3Path,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?.iamPolicy, task.transferDirection, resources, s3Path]);

  // Example boto3 code
  const exampleCode = useMemo(() => {
    const bucket = resources.bucketName || '<BUCKET_NAME>';
    const kms = resources.kmsKeyArn || '<KMS_KEY_ARN>';
    const path = s3Path || '<PATH>/';

    if (isOutbound) {
      return [
        'import boto3',
        '',
        "s3 = boto3.client('s3')",
        's3.put_object(',
        `    Bucket='${bucket}',`,
        `    Key='${path}my-file.csv',`,
        '    Body=file_data,',
        "    ServerSideEncryption='aws:kms',",
        `    SSEKMSKeyId='${kms}'`,
        ')',
      ].join('\n');
    }

    return [
      'import boto3',
      'import json',
      '',
      '# In your SQS-triggered Lambda handler:',
      'def handler(event, context):',
      "    for record in event['Records']:",
      "        message = json.loads(record['body'])",
      "        sns_message = json.loads(message['Message'])",
      '',
      "        bucket = sns_message['file']['s3Bucket']",
      "        key = sns_message['file']['s3Key']",
      '',
      "        s3 = boto3.client('s3')",
      '        response = s3.get_object(Bucket=bucket, Key=key)',
      "        file_data = response['Body'].read()",
    ].join('\n');
  }, [resources.bucketName, resources.kmsKeyArn, isOutbound, s3Path]);

  // SQS filter policy (inbound only)
  const filterPolicy = useMemo(() => {
    if (isOutbound) return null;
    return JSON.stringify({
      event_type: ['FILE_READY'],
      task_id: [task.taskId],
    }, null, 2);
  }, [isOutbound, task.taskId]);

  // Build the copyable partner email text block (dynamic based on SSH key + PGP status)
  const partnerEmailBlock = useMemo(() => {
    if (!isHostedInbound || !sftpDetails) return '';

    const lines: string[] = [
      `SFTP File Upload Instructions`,
      `Partner: ${task.partnerName || 'N/A'} | Task: ${task.name}`,
      `${'='.repeat(50)}`,
      ``,
      `Connection Details:`,
      `  Endpoint: ${sftpDetails.endpoint || 'Awaiting provisioning'}`,
      `  Port:     22`,
      `  Username: ${sftpDetails.username || 'Awaiting provisioning'}`,
      ``,
    ];

    if (sftpDetails.hasPublicKey) {
      lines.push(
        `Authentication: SSH Public Key`,
        `  Your SSH public key has been registered. You can connect immediately.`,
        ``
      );
    } else {
      lines.push(
        `Authentication: SSH Public Key (REQUIRED)`,
        `  Please generate an SSH key pair and send us your PUBLIC key.`,
        `  Accepted formats: RSA, ECDSA, or ED25519`,
        `  Example: ssh-keygen -t ed25519 -f my-sftp-key`,
        `  Send us the contents of my-sftp-key.pub`,
        `  IMPORTANT: Never send the private key file.`,
        ``
      );
    }

    lines.push(
      `Upload Directory: ${sftpDetails.uploadPath}`,
      `  Upload your files to this directory. Files will be automatically`,
      `  processed and removed from this directory after upload.`,
      ``
    );

    if (task.pgpEnabled) {
      lines.push(
        `PGP Encryption: Enabled`,
        `  All uploaded files must be PGP-encrypted before upload.`,
        ``
      );
    }

    return lines.join('\n');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHostedInbound, sftpDetails, task.partnerName, task.name, task.pgpEnabled, connection]);

  const hasAnyData = resources.bucketName || resources.snsTopicArn
    || resources.dropPath || resources.archivePath || isCreatingSns
    || isHostedInbound;

  // -------------------------------------------------------------------------
  // Not provisioned — show waiting state
  // -------------------------------------------------------------------------
  if (!hasAnyData) {
    return (
      <div className="
        bg-neutral-50 dark:bg-slate-800/50
        rounded-lg p-5
        border border-neutral-200 dark:border-slate-700
      ">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          mb-4
        ">
          {isHostedInbound ? 'Partner Integration Guide' : 'Developer Integration Guide'}
        </h4>
        <div className="
          flex items-center gap-3 p-4 rounded-lg
          bg-amber-50 dark:bg-amber-900/20
          border border-amber-200 dark:border-amber-800
        ">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500 flex-shrink-0">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Partner resources are still being provisioned. Integration details will be available once provisioning is complete.
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Hosted Inbound — SFTP partner guide (rendered ABOVE the developer guide)
  // -------------------------------------------------------------------------
  const partnerGuideSection = (isHostedInbound && sftpDetails) ? (
      <div className="
        bg-neutral-50 dark:bg-slate-800/50
        rounded-lg
        border border-neutral-200 dark:border-slate-700
      ">
        {/* Collapsible Header */}
        <button
          type="button"
          onClick={() => setShowPartnerGuide(v => !v)}
          className="flex items-center justify-between w-full p-5 text-left group"
        >
          <div className="flex items-center gap-3">
            <ChevronIcon open={showPartnerGuide} />
            <h4 className="text-base font-semibold text-neutral-800 dark:text-slate-200 group-hover:text-neutral-900 dark:group-hover:text-white transition-colors">
              Partner Integration Guide
            </h4>
          </div>
          <span className="
            inline-flex items-center px-2.5 py-1 rounded-full
            text-xs font-semibold uppercase tracking-wider
            bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800
          ">
            Hosted Inbound
          </span>
        </button>

        {showPartnerGuide && <div className="px-5 pb-5 space-y-4">
          <p className="text-sm text-neutral-600 dark:text-slate-400">
            Copy the block below and share it with the partner via email or Slack.
          </p>

          {/* Copyable email block */}
          <div className="relative">
            <pre className="
              p-4 rounded-lg text-sm font-mono leading-relaxed whitespace-pre-wrap
              bg-white dark:bg-slate-900
              border border-neutral-200 dark:border-slate-700
              text-neutral-800 dark:text-slate-200
            ">{partnerEmailBlock}</pre>
            <button
              type="button"
              onClick={async () => {
                if (await copyToClipboard(partnerEmailBlock)) {
                  setPartnerBlockCopied(true);
                  setTimeout(() => setPartnerBlockCopied(false), 2000);
                }
              }}
              className="
                absolute top-3 right-3
                flex items-center gap-1.5 px-3 py-1.5 rounded-md
                text-xs font-medium
                bg-neutral-100 dark:bg-slate-800
                hover:bg-neutral-200 dark:hover:bg-slate-700
                text-neutral-600 dark:text-slate-300
                border border-neutral-200 dark:border-slate-600
                transition-colors
              "
            >
              {partnerBlockCopied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy to Clipboard</>}
            </button>
          </div>
        </div>}
      </div>
  ) : null;

  // -------------------------------------------------------------------------
  // S3/SNS Developer Integration Guide (outbound + remote inbound + hosted inbound consumers)
  // -------------------------------------------------------------------------

  return (
    <>
    {partnerGuideSection}
    <div className={`
      bg-neutral-50 dark:bg-slate-800/50
      rounded-lg
      border border-neutral-200 dark:border-slate-700
      ${partnerGuideSection ? 'mt-4' : ''}
    `}>
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setShowDeveloperGuide(v => !v)}
        className="flex items-center justify-between w-full p-5 text-left group"
      >
        <div className="flex items-center gap-3">
          <ChevronIcon open={showDeveloperGuide} />
          <h4 className="text-base font-semibold text-neutral-800 dark:text-slate-200 group-hover:text-neutral-900 dark:group-hover:text-white transition-colors">
            Developer Integration Guide
          </h4>
        </div>
        <span className={`
          inline-flex items-center px-2.5 py-1 rounded-full
          text-xs font-semibold uppercase tracking-wider
          ${isOutbound
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
            : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
          }
        `}>
          {isOutbound ? 'Outbound' : 'Inbound'}
        </span>
      </button>

      {showDeveloperGuide && <div className="px-5 pb-5 space-y-5">
        {/* Quick Start Steps */}
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide block mb-3">
            Quick Start
          </label>
          <ol className="space-y-2.5">
            {steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="
                  flex-shrink-0 w-6 h-6 rounded-full
                  bg-primary-100 dark:bg-primary-900/30
                  text-primary-600 dark:text-primary-400
                  text-xs font-bold
                  flex items-center justify-center mt-0.5
                ">
                  {i + 1}
                </span>
                <span className="text-sm text-neutral-700 dark:text-slate-300 leading-relaxed">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* Resources */}
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wide block mb-3">
            Resources
          </label>
          <div className="space-y-3">
            <CopyableField
              label="S3 Bucket"
              value={resources.bucketName}
              placeholder="Awaiting provisioning..."
            />
            <CopyableField
              label={s3PathLabel}
              value={s3Path}
              placeholder="Awaiting provisioning..."
            />
            <CopyableField
              label="KMS Key ARN"
              value={resources.kmsKeyArn}
              placeholder="Awaiting provisioning..."
            />
            {resources.region && (
              <CopyableField label="Region" value={resources.region} />
            )}
            <SnsTopicArn
              arn={resources.snsTopicArn}
              isCreating={isCreatingSns}
            />
          </div>
        </div>

        {/* IAM Policy — collapsible */}
        <div>
          <button
            type="button"
            onClick={() => setShowPolicy(v => !v)}
            className="flex items-center gap-2 w-full text-left group"
          >
            <ChevronIcon open={showPolicy} />
            <span className="
              text-xs font-medium text-neutral-500 dark:text-slate-400
              uppercase tracking-wide
              group-hover:text-neutral-700 dark:group-hover:text-slate-200
              transition-colors
            ">
              IAM Policy
            </span>
          </button>
          {showPolicy && (
            <div className="mt-3">
              <CodeBlock
                code={JSON.stringify(iamPolicy, null, 2)}
                title="Attach to your Lambda execution role"
              />
            </div>
          )}
        </div>

        {/* Example Code — collapsible */}
        <div>
          <button
            type="button"
            onClick={() => setShowCode(v => !v)}
            className="flex items-center gap-2 w-full text-left group"
          >
            <ChevronIcon open={showCode} />
            <span className="
              text-xs font-medium text-neutral-500 dark:text-slate-400
              uppercase tracking-wide
              group-hover:text-neutral-700 dark:group-hover:text-slate-200
              transition-colors
            ">
              Example Code
            </span>
          </button>
          {showCode && (
            <div className="mt-3 space-y-3">
              <CodeBlock
                code={exampleCode}
                title={isOutbound ? 'Python - Drop a file' : 'Python - SQS-triggered Lambda'}
              />
              {filterPolicy && (
                <CodeBlock
                  code={filterPolicy}
                  title="SQS Subscription Filter Policy"
                />
              )}
            </div>
          )}
        </div>
      </div>}
    </div>
    </>
  );
}
